import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, ArrowRight, LogOut, Shield, User, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../lib/use-auth';
import Logo from '../components/Logo';

interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'standard' | 'power_user' | 'admin' | 'super_admin';
  createdAt: string;
}

interface ToolkitAccess {
  id: string;
  name: string;
  description: string;
  ctaLabel: string;
  badge?: string;
  enabled: boolean;
  adminOnly: boolean;
  launchPath: string;
}

interface Trials {
  used: number;
  limit: number;
  exhausted: boolean;
}

interface AccountData {
  user: SessionUser;
  toolkit: ToolkitAccess[];
  trials: Trials | null;
}

const BANNER_MESSAGES: Record<string, { type: 'success' | 'error'; text: string }> = {
  google_connected: { type: 'success', text: 'Google account connected successfully.' },
  tool_denied:      { type: 'error',   text: 'You don\'t have access to that tool.' },
  tool_invalid:     { type: 'error',   text: 'That tool doesn\'t exist.' },
  trial_exhausted:  { type: 'error',   text: 'You\'ve used all 3 of your free trial searches. Contact us to upgrade.' },
};

export default function Dashboard() {
  const { signOut } = useAuth();
  const [data, setData] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const accessParam = searchParams.get('access') ?? searchParams.get('oauth') ?? '';
  const banner = BANNER_MESSAGES[accessParam] ?? null;

  useEffect(() => {
    fetch('/api/account')
      .then(res => {
        if (res.status === 401) {
          window.location.href = '/auth';
          return null;
        }
        return res.json();
      })
      .then(json => {
        if (json) setData(json as AccountData);
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleLogout() {
    await signOut();
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { user, toolkit, trials } = data;
  const trialsRemaining = trials ? Math.max(0, trials.limit - trials.used) : null;
  const hasElevatedAccess = user.role !== 'standard';
  const roleLabel = user.role.replace('_', ' ');

  return (
    <div className="min-h-screen bg-surface">
      {/* Nav */}
      <nav className="border-b border-outline-variant/20 bg-surface/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <Logo className="h-9 text-primary" />
          </Link>
          <div className="flex items-center gap-4">
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm ${
              hasElevatedAccess
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container text-on-surface-variant'
            }`}>
              {hasElevatedAccess ? <Shield size={11} /> : <User size={11} />}
              {roleLabel}
            </span>
            <span className="text-sm text-on-surface-variant font-body hidden sm:block">
              {user.firstName} {user.lastName}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-on-surface-variant hover:text-primary transition-colors"
            >
              <LogOut size={14} />
              <span className="hidden sm:block">Sign out</span>
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Banner */}
        {banner && (
          <div className={`mb-8 flex items-start gap-3 px-4 py-3 rounded-sm text-sm font-body ${
            banner.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {banner.type === 'success'
              ? <CheckCircle size={16} className="mt-0.5 shrink-0" />
              : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
            {banner.text}
          </div>
        )}

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl font-headline font-semibold text-primary mb-1">
            Welcome back, {user.firstName}.
          </h1>
          <p className="text-on-surface-variant font-body">
            {user.role === 'super_admin'
              ? 'Super admin access — all tools are available.'
              : user.role === 'admin'
                ? 'Admin access — all tools are available.'
              : user.role === 'power_user'
                ? 'Power user access — all operational tools are available.'
              : trialsRemaining !== null && trialsRemaining > 0
                ? `Free trial — ${trialsRemaining} of ${trials!.limit} searches remaining.`
                : 'Your free trial has ended. Contact us to continue.'}
          </p>
        </div>

        {/* Trial meter for standard users */}
        {trials && (
          <div className="mb-10 p-5 border border-outline-variant/30 rounded-sm bg-surface-container-low">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Free Trial Usage</span>
              <span className="text-xs font-bold text-primary">{trials.used} / {trials.limit} searches used</span>
            </div>
            <div className="h-1.5 bg-surface-container-high rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${trials.exhausted ? 'bg-red-500' : 'bg-primary'}`}
                style={{ width: `${Math.min(100, (trials.used / trials.limit) * 100)}%` }}
              />
            </div>
            {trials.exhausted && (
              <p className="mt-3 text-xs text-on-surface-variant font-body">
                Trial exhausted. <a href="mailto:carter.b.xu@gmail.com" className="text-primary font-bold hover:underline">Contact us</a> to upgrade your account.
              </p>
            )}
          </div>
        )}

        {/* Toolkit grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {toolkit.filter(tool => tool.enabled).map(tool => {
            const trialBlocked = trials?.exhausted && tool.enabled;
            const isLocked = trialBlocked;

            return (
              <div
                key={tool.id}
                className={`relative flex flex-col border rounded-sm p-7 transition-all ${
                  isLocked
                    ? 'border-outline-variant/20 bg-surface-container-low opacity-60'
                    : 'border-outline-variant/30 bg-surface-container-low hover:border-primary/30 hover:bg-surface-container'
                }`}
              >
                {/* Badge */}
                {tool.badge && (
                  <span className={`absolute top-5 right-5 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm ${
                    tool.adminOnly
                      ? 'bg-primary/10 text-primary'
                      : 'bg-emerald-100 text-emerald-800'
                  }`}>
                    {tool.badge}
                  </span>
                )}

                <div className="flex-1 mb-6">
                  <h2 className="text-lg font-headline font-semibold text-primary mb-2 pr-16">
                    {tool.name}
                  </h2>
                  <p className="text-sm text-on-surface-variant font-body leading-relaxed">
                    {tool.description}
                  </p>
                </div>

                {isLocked ? (
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-on-surface-variant">
                    <Lock size={13} />
                    Trial Exhausted
                  </div>
                ) : (
                  <a
                    href={tool.launchPath}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary hover:gap-3 transition-all group"
                  >
                    {tool.ctaLabel}
                    <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <p className="mt-12 text-[11px] text-on-surface-variant/60 font-body text-center">
          All outputs should be independently verified by qualified professionals before making acquisition or development decisions.
        </p>
      </main>
    </div>
  );
}
